import { Box, Button, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ProgressBar } from '@/components/common/ProgressBar'
import type { WidgetProps } from './WidgetProps'
import { ROUTES } from '@/utils/constants'

interface AssignmentPayload {
  id: number
  name: string
  themes_total: number
  themes_done: number
}

interface CurrentThemePayload {
  id: number
  position: number
  /** null — тема скрыта политикой видимости назначения */
  title: string | null
}

/**
 * Виджет ученика «Моя программа»: текущая тема и прогресс по программе.
 */
export function MyProgramWidget({ payload }: WidgetProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const assignment = (payload.assignment ?? null) as AssignmentPayload | null
  const currentTheme = (payload.current_theme ?? null) as CurrentThemePayload | null

  if (!assignment) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('programs.my.empty', 'Преподаватель пока не назначил программу обучения.')}
      </Typography>
    )
  }

  const percent = assignment.themes_total > 0 ? (assignment.themes_done / assignment.themes_total) * 100 : 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="subtitle2" fontWeight={600} noWrap>
        {assignment.name}
      </Typography>

      {currentTheme && (
        <Typography variant="body2" color="text.secondary" noWrap>
          {t('programs.my.currentTheme', 'Текущая тема')}
          {currentTheme.title ? `: ${currentTheme.title}` : ''}
        </Typography>
      )}

      <ProgressBar value={percent} sx={{ mt: 0.5 }} />
      <Typography variant="caption" color="text.secondary">
        {t('programs.my.progress', '{{done}} из {{total}} тем', {
          done: assignment.themes_done,
          total: assignment.themes_total,
        })}
      </Typography>

      <Button
        size="small"
        sx={{ alignSelf: 'flex-start', mt: 0.5 }}
        onClick={() => navigate(ROUTES.MY_PROGRAM_VIEW(assignment.id))}
      >
        {t('programs.my.open', 'Открыть программу')}
      </Button>
    </Box>
  )
}
